import { useAuthenticator } from '@aws-amplify/ui-react';
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { FeatureFlags, useFeatureFlags, DEFAULT_FEATURE_FLAGS } from '@/config/feature-flags';

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isLoading: boolean;
  error: Error | null;
  refetchFlags: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  flags: DEFAULT_FEATURE_FLAGS,
  isLoading: true,
  error: null,
  refetchFlags: async () => {},
});

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  // The backend GET /core/feature-flags endpoint is AllowAny so pre-login screens
  // (e.g. /member/checkout) can still gate UI on flags. Fetch once auth has settled
  // in either direction; per-user overrides are merged in server-side when a token
  // is present.
  const { flags, isLoading, error, refetchFlags } = useFeatureFlags(authStatus !== 'configuring');
  const isWaitingForAuth = authStatus === 'configuring';

  const prevAuthStatusRef = useRef(authStatus);
  useEffect(() => {
    if (prevAuthStatusRef.current !== 'authenticated' && authStatus === 'authenticated') {
      void refetchFlags();
    }
    prevAuthStatusRef.current = authStatus;
  }, [authStatus, refetchFlags]);

  return (
    <FeatureFlagsContext.Provider
      value={{ flags, isLoading: isWaitingForAuth || isLoading, error, refetchFlags }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};
